import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { pickBy } from 'lodash'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { post } from 'src/client'
import { Account } from 'src/data/accounts'
import {
  Backend,
  fetchBackend,
  fetchBackends,
  refreshBackend,
  saveBackend
} from 'src/data/backends'
import { useMainChart } from 'src/data/charts'
import { useModals } from 'src/modals'

import truthy from './truthy'

const biEndpoint = 'https://winancial.biapi.pro/2.0'

export const getAppAbsoluteURL = (appRoute: string) => {
  const url = new URL(window.location.href)
  url.hash = appRoute
  url.pathname = '/'
  url.search = ''
  return url.href
}

const getBIParams = () => ({
  client_id: '92060074',
  redirect_uri: getAppAbsoluteURL('/app/settings')
})

type TokenResp = {
  code: string
  type: 'temporary'
  access: 'standard'
  expires_in: number
}

/**
 * Redirects to Powens webview with potentially a
 * temporary token generated from the permanent token of
 * the user on the server.
 */
export const redirectToWebview = async ({
  state
}: {
  state?: string
} = {}) => {
  const tokenResp = await post<TokenResp | null>('/users/bi/temporaryToken')
  const biParams = getBIParams()
  window.location.assign(
    `${biEndpoint}/auth/webview/fr/connect?${new URLSearchParams(
      pickBy(
        {
          ...biParams,
          code: tokenResp?.code || '',
          state
        },
        Boolean
      ) as Record<string, string>
    )}`
  )
}

/**
 * Redirects to Powens manage/reconnect webview
 * Generates a temporary token from the user
 */
export const redirectToManageWebview = async ({
  connectionId,
  redirectUri,
  reconnect,
  callbackState
}: {
  connectionId?: string
  redirectUri: string
  reconnect?: boolean
  callbackState?: string
}) => {
  const tokenResp = await post<TokenResp>('/users/bi/temporaryToken')
  const biParams = getBIParams()
  window.location.assign(
    `${biEndpoint}/auth/webview/fr/${
      reconnect ? 'reconnect' : 'manage'
    }?${new URLSearchParams(
      pickBy(
        {
          ...biParams,
          code: tokenResp.code,
          connection_id: connectionId,
          redirect_uri: redirectUri,
          state: callbackState
        },
        (x) => typeof x !== 'undefined'
      ) as Record<string, string>
    )}`
  )
}

type TempToken = string

const createBackend = async (
  chartId: Account['account_id'],
  connectionId: string,
  // Temporary code that we need to exchange against permanent token
  // Only in the case where the user does not have an BI account yet
  tempToken?: TempToken
): Promise<Backend['backend_id']> => {
  const newBackendId = await post<string>(
    `/charts/${chartId}/backends`,
    pickBy(
      {
        // bank_login = temporary token will be exchanged against permanent token
        // and needs to be readable by backend, thus it is not encrypted
        bank_login: tempToken,
        bank_login2: '',
        bank_password: connectionId,
        bank_name: 'budget-insight',
        next_running_date: format(new Date(), 'yyyy-MM-dd HH:mm')
      },
      truthy
    )
  )
  return parseInt(newBackendId, 10)
}

export const checkURLToSaveBIBackend = async (
  chartId: Account['account_id'],
  url: string
): Promise<
  | {
      status: 'created' | 'existing'
      backendId: number
    }
  | undefined
> => {
  const params = new URL(url).searchParams
  const connectionId = params.get('connection_id')
  if (connectionId) {
    const code = params.get('code')
    const backends = await fetchBackends(chartId)
    const existingBackend = backends.find(
      (b) => b.bank_password === connectionId
    )
    if (existingBackend) {
      console.log('Already existing backend', existingBackend)
      return {
        status: 'existing',
        backendId: existingBackend.backend_id
      }
    } else {
      return {
        status: 'created',
        backendId: await createBackend(chartId, connectionId, code || undefined)
      }
    }
  }
  const backendId = params.get('backend_id')
  if (backendId) {
    return { status: 'existing', backendId: parseInt(backendId, 10) }
  }
  return undefined
}

type BIWebviewState =
  | {
      oldBackendId: number
    }
  | undefined

export const TO_DELETE_PREFIX = `A supprimer : `

export const ensureNoToDeletePrefix = (
  backendName: Backend['backend_name']
) => {
  return backendName.replace(TO_DELETE_PREFIX, '')
}

export const hasBeenRenamedForDeletion = (backend: Backend) => {
  return backend.backend_name.includes(TO_DELETE_PREFIX)
}

const renameOldConnection = async (
  chartId: Account['account_id'],
  oldBackendId: Backend['backend_id']
) => {
  const oldBackend = await fetchBackend(chartId, oldBackendId)
  if (!oldBackend) {
    return
  }
  if (oldBackend.backend_name.includes(TO_DELETE_PREFIX)) {
    return
  }
  await saveBackend({
    ...oldBackend,
    is_enabled: false,
    backend_name: `${TO_DELETE_PREFIX}${oldBackend.backend_name}`
  })
}

const useHandleBudgetInsightCallbackURL = () => {
  const { data: chart } = useMainChart()
  const queryClient = useQueryClient()
  const { openModal } = useModals()

  const navigate = useNavigate()
  useEffect(() => {
    if (!chart) {
      return
    }
    const run = async () => {
      const checkResult = await checkURLToSaveBIBackend(
        chart?.account_id,
        window.location.href
      )
      // Remove connectionId from URL
      queryClient.invalidateQueries(['backends'])
      const currentURL = new URL(window.location.href)
      const stateParam = currentURL.searchParams.get('state')
      if (checkResult?.status === 'created') {
        const createdBackendId = checkResult.backendId
        console.log('Will clean URL', currentURL)
        window.history.replaceState(null, '', '/')
        const state: BIWebviewState = stateParam
          ? JSON.parse(stateParam)
          : undefined
        if (state?.oldBackendId) {
          await renameOldConnection(chart.account_id, state.oldBackendId)
          queryClient.invalidateQueries(['backends'])
        }
        navigate(`/app/settings/backends/${createdBackendId}`)
        openModal('ConfigureBackend', {
          backendId: createdBackendId,
          oldBackendId: state?.oldBackendId
        })
      } else if (checkResult?.status === 'existing') {
        console.log('Asking to refresh backend')
        await refreshBackend(chart.account_id, checkResult.backendId)
        await queryClient.invalidateQueries(['backends'])
      }
    }
    run()
  }, [chart, window.location])
}

/**
 * We need to have a fake component so that we can run the hook
 * in the context of the query client
 */
export const PowensCallbackCheck = () => {
  useHandleBudgetInsightCallbackURL()
  return null
}
